import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx-js-style';

function getItemTypeLabel(type) {
  switch (type) {
    case 1: return 'Service';
    case 2: return 'Retail';
    case 3: return 'Sundry';
    case 5: return 'Gift';
    case 6: return 'Series';
    case 7: return 'Membership';
    case 8: return 'Promotion';
    default: return '-';
  }
}
function setNumberFormat(ws, rowIndex, colIndex) {
  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
  if (ws[cellAddress]) {
    ws[cellAddress].z = '#,##0.00'; // 👈 comma format
  }
}

export function generateSummaryPdf({ summary, transactions, filters }) {
  const doc = new jsPDF();

  doc.setFontSize(12);
  doc.text('Commission Summary', 14, 18);

  doc.setFontSize(10);
  const fromDate = new Date(filters.from).toISOString().split('T')[0];
  const toDate = new Date(filters.to).toISOString().split('T')[0];
  doc.text(`Period: ${fromDate} - ${toDate}`, 14, 26);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);

  const rows = [];
  

  summary.forEach(stylist => {
    const itemTypes = [
  ...new Set(
    transactions
      .filter(t => 
        t.OriginalEmployeeId === stylist.EmployeeId &&
        t.Role === 'Stylist'
      )
      .map(t => t.ItemType)
  )
];

const itemTypeLabels = itemTypes.length
  ? itemTypes.map(getItemTypeLabel).join(', ')
  : '-';
    // 🔹 Stylist row (parent)
    rows.push([
      stylist.EmployeeName,
      'Stylist',
       itemTypeLabels,
      stylist.TotalSalesExTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      stylist.StylistCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      stylist.TotalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    ]);

    // 🔹 Operator rows (children)
    stylist.Operators?.forEach(op => {
      const opItemTypes = [
  ...new Set(
    transactions
      .filter(t => 
        t.EmployeeId === op.EmployeeId &&
t.OriginalEmployeeId === stylist.EmployeeId && // 🔥 IMPORTANT
t.Role === 'Operator'
      )
      .map(t => t.ItemType)
  )
];

const opItemTypeLabels = opItemTypes.length
  ? opItemTypes.map(getItemTypeLabel).join(', ')
  : '-';
      rows.push([
        `  - ${op.EmployeeName}`,
        'Operator',
        opItemTypeLabels,
        '',
        op.TotalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        '',
      ]);
    });
  });
  // 🔹 Add Grand Total row AFTER the loop
const totalSales = summary.reduce((acc, s) => acc + (s.TotalSalesExTax || 0), 0);
const totalCommission = summary.reduce((acc, s) => acc + (s.TotalCommission || 0), 0);


rows.push([
  'Grand Total',   // Employee
  '',  //Role
  '',            // Item Type
  totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  totalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  '',
]);

  autoTable(doc, {
    theme: 'grid',
    startY: 40,
    head: [
      ['Employee', 'Role','Item Type', 'Total Sales', 'Commission Earned', 'Split Total']
    ],
    body: rows,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: 20,
      fontStyle: 'bold',
    },
    columnStyles: {
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
    },
    didParseCell(data) {
  // Operator rows styling
  if (data.row.raw?.[1] === 'Operator') {
    data.cell.styles.textColor = [20, 120, 80];
    data.cell.styles.fontStyle = 'italic';
  }

  // 👉 Header-only fine tuning (NO data affected)
if (data.section === 'head' && [2, 3, 4].includes(data.column.index)) {
  data.cell.styles.cellPadding = {
    left: 10,   // ⬅️ increase to move RIGHT
    right: 3,
    top: 3,
    bottom: 3,
  };
}
// Bold totals row and give light gray background
if (data.row.index === rows.length - 1) {
  data.cell.styles.fontStyle = 'bold';
  data.cell.styles.fillColor = [230, 230, 230]; // light gray background
}


},
  });

  doc.save('commission-summary.pdf');
}

export function generateDetailedPdf({ summary, transactions, expandedEmployeeId, expandedFilters, filters }) {
  const doc = new jsPDF();

  summary.forEach((employee, index) => {
    if (index !== 0) doc.addPage();

    doc.setFontSize(12);
    doc.text(`Commission Details – ${employee.EmployeeName}`, 14, 18);

    doc.setFontSize(10);
    const fromDate = new Date(filters.from).toISOString().split('T')[0];
    const toDate = new Date(filters.to).toISOString().split('T')[0];
    doc.text(`Period: ${fromDate} - ${toDate}`, 14, 26);

    const activeFilters = expandedEmployeeId === employee.EmployeeId
      ? expandedFilters[employee.EmployeeId]
      : {};

    const baseFiltered = transactions
  .filter(t => t.OriginalEmployeeId === employee.EmployeeId)
  .filter(t => {
    if (activeFilters?.ruleId && t.CommissionRuleId !== activeFilters.ruleId) return false;

    if (activeFilters?.company && t.ItemType === 2 && t.ItemName !== activeFilters.company) return false;

    if (
      activeFilters?.category &&
      [1, 7, 8].includes(t.ItemType) &&
      t.ItemName !== activeFilters.category
    ) return false;

    return true;
  });

const filteredTx = baseFiltered.reduce((acc, t) => {
  if (activeFilters?.operator) {
    const saleHasOperator = baseFiltered.some(
      x =>
        x.SaleID === t.SaleID &&
        x.Role === 'Operator' &&
        x.EmployeeName === activeFilters.operator
    );

    if (!saleHasOperator) return acc;
  }

  acc.push(t);
  return acc;
}, []);

    // 🔁 Build rows grouped by SaleID (stylist → operators)
const rows = [];

const grouped = filteredTx.reduce((acc, t) => {
  if (!acc[t.SaleID]) acc[t.SaleID] = [];
  acc[t.SaleID].push(t);
  return acc;
}, {});

Object.values(grouped).forEach(saleRows => {
 
  const operators = saleRows.filter(r => r.Role === 'Operator');
  const stylists = saleRows.filter(r => r.Role === 'Stylist');

stylists.forEach(stylist => {
  rows.push([
    stylist.TransactionDate,
    stylist.ItemName || '—',
    stylist.RuleName,
    stylist.SplitInfo || '—',
    stylist.TotalSalesExTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    stylist.CommissionAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  ]);
});

  

  // Child rows (Operators)
  operators.forEach(op => {
    rows.push([
      '',
      `  - ${op.EmployeeName}`,
      op.RuleName,
      op.SplitInfo || '—',
      '',
      op.CommissionAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    ]);
  });
});
// Per-employee totals
  const employeeTotalSales = filteredTx.reduce((acc, t) => acc + (t.TotalSalesExTax || 0), 0);
  const employeeTotalCommission = filteredTx.reduce((acc, t) => acc + (t.CommissionAmount || 0), 0);

  rows.push([
    '', 
    'Employee Total', 
    '', 
    '', 
    employeeTotalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 
    employeeTotalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  ]);

    autoTable(doc, {
  theme: 'grid', // 👈 THIS removes striping completely
  startY: 32,
  head: [['Date', 'Item', 'Rule', 'Split', 'Total Sales', 'Commission']],
  body: rows,
  styles: { fontSize: 9, cellPadding: 3 },
  headStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
  columnStyles: {
    4: { halign: 'right' },
    5: { halign: 'right' },
  },
      didParseCell: function (data) {
        // Center the data under Split, Total Sales, and Commission headers
        if (data.column.index === 3 || data.column.index === 4 || data.column.index === 5) {
          data.cell.styles.halign = 'center';
        }
        if (data.row.raw?.[1] === 'Employee Total') {
  data.cell.styles.fontStyle = 'bold';
  data.cell.styles.fillColor = [240, 240, 240]; // light gray
}
// Operator rows (child rows)
if (data.row.raw?.[1]?.startsWith('  -')) {
  data.cell.styles.textColor = [30, 130, 90]; // softer green
  data.cell.styles.fontStyle = 'italic';
}

      },
      margin: { left: 14 },
    });
  });
  // 🔹 Overall totals AFTER all tables
const overallTotalSales = transactions.reduce((acc, t) => acc + (t.TotalSalesExTax || 0), 0);
const overallTotalCommission = transactions.reduce((acc, t) => acc + (t.CommissionAmount || 0), 0);

doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.text(
  `Overall Total Sales: ${overallTotalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Overall Total Commission: ${overallTotalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  14,
  doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 40
);

  doc.save('commission-detailed.pdf');
}


export function generateSummaryExcel({ summary, transactions, filters }) {
  const fromDate = new Date(filters.from).toISOString().split('T')[0];
  const toDate = new Date(filters.to).toISOString().split('T')[0];

  const rows = [];

  rows.push(['Commission Summary']);
  rows.push([`Period: ${fromDate} - ${toDate}`]);
  rows.push([`Generated on: ${new Date().toLocaleString()}`]);
  rows.push([]);

  rows.push(['Employee', 'Role', 'Item Type', 'Total Sales', 'Commission Earned', 'Split Total']);

  summary.forEach(stylist => {
    const itemTypes = [
      ...new Set(
        transactions
          .filter(t =>
            t.OriginalEmployeeId === stylist.EmployeeId &&
            t.Role === 'Stylist'
          )
          .map(t => t.ItemType)
      )
    ];

    const itemTypeLabels = itemTypes.length
      ? itemTypes.map(getItemTypeLabel).join(', ')
      : '-';

    rows.push([
      stylist.EmployeeName,
      'Stylist',
      itemTypeLabels,
      stylist.TotalSalesExTax || 0,
      stylist.StylistCommission || 0,
      stylist.TotalCommission || 0,
    ]);

    stylist.Operators?.forEach(op => {
      const opItemTypes = [
        ...new Set(
          transactions
            .filter(t =>
              t.EmployeeId === op.EmployeeId &&
              t.OriginalEmployeeId === stylist.EmployeeId &&
              t.Role === 'Operator'
            )
            .map(t => t.ItemType)
        )
      ];

      const opItemTypeLabels = opItemTypes.length
        ? opItemTypes.map(getItemTypeLabel).join(', ')
        : '-';

      rows.push([
        `  - ${op.EmployeeName}`,
        'Operator',
        opItemTypeLabels,
        '',
        op.TotalCommission || 0,
        '',
      ]);
    });
  });

  const totalSales = summary.reduce((acc, s) => acc + (s.TotalSalesExTax || 0), 0);
  const totalCommission = summary.reduce((acc, s) => acc + (s.TotalCommission || 0), 0);

  rows.push([]);
  rows.push([
    'Grand Total',
    '',
    '',
    totalSales,
    totalCommission,
    ''
  ]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  // Apply number formatting
rows.forEach((row, rowIndex) => {
  // Total Sales (col 3), Commission (col 4), Split (col 5)
  [3, 4, 5].forEach(colIndex => {
    if (typeof row[colIndex] === 'number') {
      setNumberFormat(ws, rowIndex, colIndex);
    }
  });
});

// 🎨 HEADER STYLING
const headerRowIndex = 4; // your header is here

const range = XLSX.utils.decode_range(ws['!ref']);

for (let col = range.s.c; col <= range.e.c; col++) {
  const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });

  if (ws[cellAddress]) {
    ws[cellAddress].s = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "4F81BD" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };
  }
}

// ✨ COLUMN WIDTHS
ws['!cols'] = [
  { wch: 25 },
  { wch: 12 },
  { wch: 25 },
  { wch: 15 },
  { wch: 20 },
  { wch: 15 },
];

// 💰 GRAND TOTAL STYLING
const totalRowIndex = rows.length - 1;

for (let col = 0; col < 6; col++) {
  const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });

  if (ws[cellAddress]) {
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "E7E6E6" } },
    };
  }
}

const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, 'Summary');

  XLSX.writeFile(wb, 'commission-summary.xlsx');
}




export function generateDetailedExcel({ summary, transactions, expandedEmployeeId, expandedFilters, filters }) {
  const wb = XLSX.utils.book_new();

  summary.forEach(employee => {
    const fromDate = new Date(filters.from).toISOString().split('T')[0];
    const toDate = new Date(filters.to).toISOString().split('T')[0];

    const rows = [];

    rows.push([`Commission Details – ${employee.EmployeeName}`]);
    rows.push([`Period: ${fromDate} - ${toDate}`]);
    rows.push([]);

    rows.push(['Date', 'Item', 'Rule', 'Split', 'Total Sales', 'Commission']);

    const activeFilters = expandedEmployeeId === employee.EmployeeId
      ? expandedFilters[employee.EmployeeId]
      : {};

    const baseFiltered = transactions
  .filter(t => t.OriginalEmployeeId === employee.EmployeeId)
  .filter(t => {
    if (activeFilters?.ruleId && t.CommissionRuleId !== activeFilters.ruleId) return false;

    if (activeFilters?.company && t.ItemType === 2 && t.ItemName !== activeFilters.company) return false;

    if (
      activeFilters?.category &&
      [1, 7, 8].includes(t.ItemType) &&
      t.ItemName !== activeFilters.category
    ) return false;

    return true;
  });

const filteredTx = baseFiltered.reduce((acc, t) => {
  if (activeFilters?.operator) {
    const saleHasOperator = baseFiltered.some(
      x =>
        x.SaleID === t.SaleID &&
        x.Role === 'Operator' &&
        x.EmployeeName === activeFilters.operator
    );

    if (!saleHasOperator) return acc;
  }

  acc.push(t);
  return acc;
}, []);

    const grouped = filteredTx.reduce((acc, t) => {
      if (!acc[t.SaleID]) acc[t.SaleID] = [];
      acc[t.SaleID].push(t);
      return acc;
    }, {});

    Object.values(grouped).forEach(saleRows => {
      
      const operators = saleRows.filter(r => r.Role === 'Operator');

     const stylists = saleRows.filter(r => r.Role === 'Stylist');

stylists.forEach(stylist => {
  rows.push([
    stylist.TransactionDate,
    stylist.ItemName || '—',
    stylist.RuleName,
    stylist.SplitInfo || '—',
    stylist.TotalSalesExTax || 0,
    stylist.CommissionAmount || 0,
  ]);
});

      operators.forEach(op => {
        rows.push([
          '',
          `  - ${op.EmployeeName}`,
          op.RuleName,
          op.SplitInfo || '—',
          '',
          op.CommissionAmount || 0,
        ]);
      });
    });

    const employeeTotalSales = filteredTx.reduce((acc, t) => acc + (t.TotalSalesExTax || 0), 0);
    const employeeTotalCommission = filteredTx.reduce((acc, t) => acc + (t.CommissionAmount || 0), 0);

    rows.push([]);
    rows.push([
      '',
      'Employee Total',
      '',
      '',
      employeeTotalSales,
      employeeTotalCommission,
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Apply number formatting
rows.forEach((row, rowIndex) => {
  // Total Sales (col 3), Commission (col 4), Split (col 5)
  [3, 4, 5].forEach(colIndex => {
    if (typeof row[colIndex] === 'number') {
      setNumberFormat(ws, rowIndex, colIndex);
    }
  });
});

// 🎨 HEADER STYLING
const headerRowIndex = 3; // different here!

const range = XLSX.utils.decode_range(ws['!ref']);

for (let col = range.s.c; col <= range.e.c; col++) {
  const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });

  if (ws[cellAddress]) {
    ws[cellAddress].s = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "4F81BD" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };
  }
}

// ✨ COLUMN WIDTHS
ws['!cols'] = [
  { wch: 15 },
  { wch: 25 },
  { wch: 25 },
  { wch: 20 },
  { wch: 15 },
  { wch: 15 },
];

// 💰 EMPLOYEE TOTAL ROW STYLING
const totalRowIndex = rows.length - 1;

for (let col = 0; col < 6; col++) {
  const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });

  if (ws[cellAddress]) {
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "E7E6E6" } },
    };
  }
}

    // Sheet name max 31 chars (Excel limit)
    const sheetName = employee.EmployeeName.substring(0, 30);

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // 🔹 Overall totals sheet
  const overallTotalSales = transactions.reduce((acc, t) => acc + (t.TotalSalesExTax || 0), 0);
  const overallTotalCommission = transactions.reduce((acc, t) => acc + (t.CommissionAmount || 0), 0);

  const summaryRows = [
  ['Overall Totals'],
  [],
  ['Metric', 'Amount'],
  ['Total Sales', overallTotalSales],
  ['Total Commission', overallTotalCommission],
];

const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);

// Apply number formatting
summaryRows.forEach((row, rowIndex) => {
  // Total Sales (col 1), Total Commission (col 2)
  [1, 2].forEach(colIndex => {
    if (typeof row[colIndex] === 'number') {
      setNumberFormat(summarySheet, rowIndex, colIndex);
    }
  });
});

// 🎨 HEADER STYLING (Metric / Amount)
const headerRowIndex = 2;

for (let col = 0; col < 2; col++) {
  const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: col });

  if (summarySheet[cellAddress]) {
    summarySheet[cellAddress].s = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "4F81BD" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };
  }
}

// ✨ COLUMN WIDTHS
summarySheet['!cols'] = [
  { wch: 25 },
  { wch: 20 },
];

// 💰 TOTAL ROW STYLING
for (let row = 3; row <= 4; row++) {
  for (let col = 0; col < 2; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });

    if (summarySheet[cellAddress]) {
      summarySheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "E7E6E6" } },
      };
    }
  }
}

// 🏷️ TITLE STYLING (Overall Totals)
const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });

if (summarySheet[titleCell]) {
  summarySheet[titleCell].s = {
    font: { bold: true, sz: 14 },
  };
}

  XLSX.utils.book_append_sheet(wb, summarySheet, 'Overall');

  XLSX.writeFile(wb, 'commission-detailed.xlsx');
}